// ─── Personal Insights Panel ─────────────────────────────────────────────────
//
// Displays weekly auto-generated health insights in a single clean card.
// Each row: color accent bar | category icon | title + detail | value badge.
// Shows top 3 by default; expands to show all 6 on demand.
// Returns null when fewer than 2 insights are available (no empty state shown).
//
// Insight examples:
//   • "HRV trending down" / "-12%" (amber)
//   • "5-day movement streak" / "5 days" (green)
//   • "Vitamin D is deficient" / "18 ng/mL" (red)
//   • "Exercise boosts your sleep" / "+9 pts" (green)
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Insight, InsightCategory } from '../lib/insights';
import { useLens } from '../lib/dashboard/lensContext';
import { insightCopy } from '../lib/dashboard/insightLensCopy';
import type { Lens } from '../lib/dashboard/types';
import {
  IconHeartPulse, IconMoon, IconShoe, IconFlame,
  IconZap, IconBrain, IconFlask, IconActivity,
} from './Icons';

// ─── Category Icon ────────────────────────────────────────────────────────────

function CategoryIcon({ category, color }: { category: InsightCategory; color: string }) {
  const p = { size: 14 as const, color };
  switch (category) {
    case 'hrv':         return <IconHeartPulse {...p} />;
    case 'sleep':       return <IconMoon       {...p} />;
    case 'movement':    return <IconShoe       {...p} />;
    case 'streak':      return <IconFlame      {...p} />;
    case 'stress':      return <IconZap        {...p} />;
    case 'mood':        return <IconBrain      {...p} />;
    case 'lab':         return <IconFlask      {...p} />;
    case 'correlation': return <IconActivity   {...p} />;
    default:            return <IconActivity   {...p} />;
  }
}

// ─── Color Mapping ────────────────────────────────────────────────────────────

/** Returns the accent color for a given insight based on direction + clinical priority. */
function accentColor(ins: Insight): string {
  if (ins.category === 'correlation') return '#6366F1'; // indigo for correlations
  if (ins.direction === 'positive') return '#22C55E';
  if (ins.direction === 'negative') return ins.priority >= 8 ? '#EF4444' : '#F59E0B';
  return '#94A3B8';
}

/** Map insight IDs to their constituent metric categories for dual-icon display */
const CORRELATION_ICONS: Record<string, [InsightCategory, InsightCategory]> = {
  'exercise-sleep': ['movement', 'sleep'],
  'sleep-hrv':      ['sleep', 'hrv'],
  'steps-stress':   ['movement', 'stress'],
};

// ─── Single Insight Row ───────────────────────────────────────────────────────

function InsightRow({ insight, lens }: { insight: Insight; lens: Lens }) {
  const color = accentColor(insight);
  const copy = insightCopy(insight, lens);

  const content = (
    <div className="flex items-start gap-3 py-2.5">
      {/* Left accent bar */}
      <div
        className="w-0.5 rounded-full flex-shrink-0 self-stretch"
        style={{ backgroundColor: color, minHeight: 32 }}
      />

      {/* Category icon(s) — dual icons for correlations */}
      <div className="mt-0.5 flex-shrink-0 flex items-center gap-0.5">
        {insight.category === 'correlation' && CORRELATION_ICONS[insight.id] ? (
          <>
            <CategoryIcon category={CORRELATION_ICONS[insight.id][0]} color={color} />
            <span className="text-[9px] text-arete-muted">↔</span>
            <CategoryIcon category={CORRELATION_ICONS[insight.id][1]} color={color} />
          </>
        ) : (
          <CategoryIcon category={insight.category} color={color} />
        )}
      </div>

      {/* Text block */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-sm font-semibold text-arete-navy dark:text-gray-100 leading-tight">
            {copy.title}
          </p>
          <span
            className="text-sm font-bold tabular-nums flex-shrink-0"
            style={{ color }}
          >
            {insight.value}
          </span>
        </div>
        <p className="text-xs text-arete-muted dark:text-gray-400 mt-0.5 leading-relaxed">
          {copy.detail}
        </p>
      </div>
    </div>
  );

  // Wrap in a Link if a deep-link destination is provided
  if (insight.link) {
    return (
      <Link
        to={insight.link}
        className="block hover:bg-gray-50 dark:hover:bg-dark-card/50 rounded-xl transition-colors no-underline -mx-1 px-1"
      >
        {content}
      </Link>
    );
  }
  return <div className="-mx-1 px-1">{content}</div>;
}

// ─── Panel ────────────────────────────────────────────────────────────────────

interface InsightsPanelProps {
  insights: Insight[];
}

export default React.memo(function InsightsPanel({ insights }: InsightsPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const { lens } = useLens();

  // Don't render if there's nothing meaningful to show
  if (insights.length < 2) return null;

  const shown      = expanded ? insights : insights.slice(0, 3);
  const hiddenCount = Math.max(0, insights.length - 3);

  // Lens-aware section header + footer. Engine output is unchanged; only
  // the framing text switches between patient warmth and clinical precision.
  const sectionLabel = lens === 'clinician' ? 'Weekly Signals' : 'This Week';
  const footer = lens === 'clinician'
    ? 'Auto-generated from 14-day signal window · clinician view'
    : 'Generated from your last 14 days of data';

  return (
    <div
      className="card"
      data-testid="insights-panel"
      data-lens={lens}
      data-phase="6"
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {/* Sparkle / star icon */}
          <svg
            width={13} height={13} viewBox="0 0 24 24"
            fill="#D4A843" stroke="none"
          >
            <path d="M12 2l2.09 6.26L20 10l-5.91 1.74L12 18l-2.09-6.26L4 10l5.91-1.74z" />
          </svg>
          <h2 className="section-label">
            {sectionLabel}
          </h2>
        </div>

        {/* Expand / collapse toggle */}
        {hiddenCount > 0 && (
          <button
            onClick={() => setExpanded(e => !e)}
            className="text-xs font-medium text-arete-blue hover:underline"
          >
            {expanded ? 'Show less' : `+${hiddenCount} more`}
          </button>
        )}
      </div>

      {/* ── Insight rows ── */}
      <div className="divide-y divide-gray-100/60 dark:divide-gray-700/40">
        {shown.map(ins => (
          <InsightRow key={ins.id} insight={ins} lens={lens} />
        ))}
      </div>

      {/* ── Footer ── */}
      <p className="text-[10px] text-arete-muted/70 dark:text-gray-500 mt-2 pt-2 border-t border-gray-100/60 dark:border-gray-700/40">
        {footer}
      </p>
    </div>
  );
});
